import { useState } from "react";
import { Pin, PinOff, TrendingUp, RefreshCw } from "lucide-react";
import { adminApi } from "../../api";
import { LoadingText, Select } from "../ui";
import { useAsync } from "../../hooks";

interface PinnedQuestion {
  id: string;
  question: string;
}

interface TrendingQuestion {
  question: string;
  count: number;
  unique_users: number;
  already_pinned?: boolean;
}

export default function TrendingQuestions() {
  const [days, setDays] = useState(30);
  const [actionId, setActionId] = useState<string | null>(null);

  const { data, loading, error: loadError, reload } = useAsync<{ trending: TrendingQuestion[]; pinned: PinnedQuestion[] }>(
    () =>
      Promise.all([
        adminApi.getTrendingQuestions(days),
        adminApi.getSuggestedQuestions(),
      ]).then(([trendRes, pinnedRes]) => ({
        trending: trendRes.data ?? [],
        pinned: pinnedRes.data ?? [],
      })),
    [days],
    { errorMessage: "Failed to load questions." },
  );
  const trending = data?.trending ?? [];
  const pinned = data?.pinned ?? [];
  const error = loadError;

  async function handlePin(question: string) {
    setActionId(question);
    try {
      await adminApi.pinQuestion(question);
      reload();
    } catch { return; } finally {
      setActionId(null);
    }
  }

  async function handleUnpin(id: string) {
    setActionId(id);
    try {
      await adminApi.unpinQuestion(id);
      reload();
    } catch { return; } finally {
      setActionId(null);
    }
  }

  return (
    <div className="space-y-6">
      {/* Pinned suggested questions */}
      <div>
        <h3 className="mb-3 text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Pin size={14} className="text-blue-500" />
          Pinned suggested questions
          <span className="ml-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-600">
            {pinned.length}
          </span>
        </h3>

        {pinned.length === 0 ? (
          <p className="text-sm text-gray-400 italic">
            No questions pinned yet. Pin trending questions below to show them in the chat.
          </p>
        ) : (
          <ul className="space-y-2">
            {pinned.map((q) => (
              <li key={q.id} className="flex items-center justify-between rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm">
                <span className="text-gray-800">{q.question}</span>
                <button
                  onClick={() => handleUnpin(q.id)}
                  disabled={actionId === q.id}
                  title="Unpin"
                  className="ml-3 shrink-0 text-gray-400 hover:text-red-500 disabled:opacity-40"
                >
                  <PinOff size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Trending questions */}
      <div>
        <div className="mb-3 flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <TrendingUp size={14} className="text-green-500" />
            Trending questions
          </h3>
          <Select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            size="sm"
            className="w-auto min-w-32"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </Select>
          <button
            onClick={reload}
            disabled={loading}
            className="ml-auto text-gray-400 hover:text-gray-600 disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        {error && <p className="mb-2 text-sm text-red-500">{error}</p>}

        {loading ? (
          <LoadingText>Loading...</LoadingText>
        ) : trending.length === 0 ? (
          <p className="text-sm text-gray-400 italic">
            No questions found in the last {days} days.
          </p>
        ) : (
          <ul className="space-y-2">
            {trending.map(({ question, count, unique_users, already_pinned }) => (
              <li key={question} className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700" title={`${unique_users} unique ${unique_users === 1 ? "user" : "users"}, ${count} total`}>
                    {unique_users} {unique_users === 1 ? "user" : "users"}
                  </span>
                  {count > unique_users && (
                    <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                      ×{count}
                    </span>
                  )}
                  <span className="truncate text-gray-800">{question}</span>
                </div>
                <button
                  onClick={() => handlePin(question)}
                  disabled={already_pinned || actionId === question}
                  title={already_pinned ? "Already pinned" : "Pin as suggested question"}
                  className={`ml-3 shrink-0 ${already_pinned ? "text-blue-400 cursor-default" : "text-gray-400 hover:text-blue-500 disabled:opacity-40"}`}
                >
                  <Pin size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
